import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  SlidersHorizontal,
  Pill,
  X,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

import api, { eur } from "../lib/api";
import ProductCard from "../components/storefront/ProductCard";

import {
  scoreProduct,
  deriveAttributes,
  SKIN_LABELS,
  FLAG_LABELS,
} from "../lib/catalog";

import { Slider } from "../components/ui/slider";

import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetHeader,
  SheetTitle,
} from "../components/ui/sheet";

const SORTS = [
  { key: "relevance", label: "Σχετικότητα" },
  { key: "price_asc", label: "Τιμή: Αύξουσα" },
  { key: "price_desc", label: "Τιμή: Φθίνουσα" },
  { key: "discount", label: "Μεγαλύτερη έκπτωση" },
  { key: "name", label: "Αλφαβητικά" },
];

const PAGE = 12;

export default function Shop() {
  const [params, setParams] = useSearchParams();

  const [all, setAll] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "";
  const search = params.get("search") || "";

  const [brands, setBrands] = useState(new Set());
  const [skin, setSkin] = useState(new Set());
  const [flags, setFlags] = useState(new Set());
  const [spf, setSpf] = useState(new Set());

  const [inStock, setInStock] = useState(false);
  const [onOffer, setOnOffer] = useState(false);
  const [price, setPrice] = useState(null);
  const [sort, setSort] = useState("relevance");
  const [visible, setVisible] = useState(PAGE);

  const [expandedCategories, setExpandedCategories] = useState(
    new Set()
  );

  const sentinel = useRef(null);

  // =========================================================
  // LOAD PRODUCTS + CATEGORIES
  // =========================================================

  useEffect(() => {
    setLoading(true);

    Promise.all([
      api.get("/products").then(({ data }) => setAll(data)),
      api.get("/categories").then(({ data }) => setCategories(data)),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  // =========================================================
  // CATEGORY MAP
  // =========================================================

  const categoryMap = useMemo(() => {
    const map = {};

    categories.forEach((c) => {
      map[c.id] = c;
    });

    return map;
  }, [categories]);

  // =========================================================
  // CHILDREN MAP
  // =========================================================

  const childrenMap = useMemo(() => {
    const map = {};

    categories.forEach((c) => {
      const parentId = c.parent_id || "";

      if (!map[parentId]) {
        map[parentId] = [];
      }

      map[parentId].push(c);
    });

    return map;
  }, [categories]);

  // =========================================================
  // GET CATEGORY TREE IDS
  // =========================================================

  const getCategoryTreeIds = (categoryId) => {
    const ids = [];
    const queue = [categoryId];
    const visited = new Set();

    while (queue.length > 0) {
      const currentId = queue.shift();

      if (!currentId || visited.has(currentId)) {
        continue;
      }

      visited.add(currentId);
      ids.push(currentId);

      const children = childrenMap[currentId] || [];

      children.forEach((child) => {
        if (!visited.has(child.id)) {
          queue.push(child.id);
        }
      });
    }

    return ids;
  };

  // =========================================================
  // PRODUCT CATEGORY HELPERS
  // =========================================================

  const getProductCategoryIds = (product) => {
    if (
      Array.isArray(product.category_ids) &&
      product.category_ids.length > 0
    ) {
      return product.category_ids;
    }

    if (product.category_id) {
      return [product.category_id];
    }

    return [];
  };

  const productBelongsToCategory = (product, categoryId) => {
    if (!categoryId) {
      return true;
    }

    const productCategoryIds = getProductCategoryIds(product);
    const validIds = getCategoryTreeIds(categoryId);

    return productCategoryIds.some((id) =>
      validIds.includes(id)
    );
  };

  // =========================================================
  // CATEGORY PRODUCT COUNT
  // =========================================================

  const categoryProductCount = (categoryId) => {
    return all.filter((p) =>
      productBelongsToCategory(p, categoryId)
    ).length;
  };

  // =========================================================
  // PRODUCT ATTRIBUTES
  // =========================================================

  const attrs = useMemo(() => {
    const map = {};

    all.forEach((p) => {
      map[p.id] = deriveAttributes(p);
    });

    return map;
  }, [all]);

  // =========================================================
  // ORDER CATEGORIES
  // =========================================================

  const orderedCategories = useMemo(() => {
    const result = [];
    const visited = new Set();

    const roots = categories.filter(
      (c) => !c.parent_id
    );

    const stack = [];

    for (let i = roots.length - 1; i >= 0; i--) {
      stack.push({
        category: roots[i],
        level: 0,
      });
    }

    while (stack.length > 0) {
      const item = stack.pop();

      if (!item || !item.category) {
        continue;
      }

      const c = item.category;

      if (visited.has(c.id)) {
        continue;
      }

      visited.add(c.id);

      result.push({
        ...c,
        level: item.level,
      });

      const children = childrenMap[c.id] || [];

      for (let i = children.length - 1; i >= 0; i--) {
        if (!visited.has(children[i].id)) {
          stack.push({
            category: children[i],
            level: item.level + 1,
          });
        }
      }
    }

    categories.forEach((c) => {
      if (!visited.has(c.id)) {
        result.push({
          ...c,
          level: 0,
        });
      }
    });

    return result;
  }, [categories, childrenMap]);

  // =========================================================
  // CHECK CATEGORY VISIBILITY
  // =========================================================

  const isCategoryVisible = (c) => {
    if (!c.parent_id) {
      return true;
    }

    let parentId = c.parent_id;
    const visited = new Set();

    while (parentId) {
      if (visited.has(parentId)) {
        return false;
      }

      visited.add(parentId);

      if (!expandedCategories.has(parentId)) {
        return false;
      }

      const parent = categoryMap[parentId];

      if (!parent) {
        return true;
      }

      parentId = parent.parent_id || "";
    }

    return true;
  };

  // =========================================================
  // TOGGLE CATEGORY
  // =========================================================

  const toggleCategoryOpen = (categoryId) => {
    setExpandedCategories((previous) => {
      const next = new Set(previous);

      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }

      return next;
    });
  };

  // =========================================================
  // OPEN PARENT CATEGORIES
  // =========================================================

  useEffect(() => {
    if (!category) {
      return;
    }

    const parentsToOpen = new Set();
    const visited = new Set();

    let currentId = category;

    while (currentId) {
      if (visited.has(currentId)) {
        break;
      }

      visited.add(currentId);

      const current = categoryMap[currentId];

      if (!current || !current.parent_id) {
        break;
      }

      parentsToOpen.add(current.parent_id);
      currentId = current.parent_id;
    }

    if (parentsToOpen.size > 0) {
      setExpandedCategories((previous) => {
        const next = new Set(previous);

        parentsToOpen.forEach((id) => {
          next.add(id);
        });

        return next;
      });
    }
  }, [category, categoryMap]);

  // =========================================================
  // FILTER BY CATEGORY + SEARCH
  // =========================================================

  const scoped = useMemo(() => {
    let list = all;

    if (category) {
      list = list.filter((p) =>
        productBelongsToCategory(p, category)
      );
    }

    if (search) {
      list = list
        .map((p) => ({
          p,
          s: scoreProduct(p, search),
        }))
        .filter((x) => x.s >= 0)
        .sort((a, b) => b.s - a.s)
        .map((x) => x.p);
    }

    return list;
  }, [all, category, search, childrenMap]);

  // =========================================================
  // FACETS
  // =========================================================

  const facets = useMemo(() => {
    const b = new Map();
    const sk = new Set();
    const fl = new Set();
    const sp = new Set();

    let min = Infinity;
    let max = 0;

    scoped.forEach((p) => {
      if (p.brand) {
        b.set(
          p.brand,
          (b.get(p.brand) || 0) + 1
        );
      }

      const a = attrs[p.id] || {};

      (a.skin || []).forEach((x) => sk.add(x));
      (a.flags || []).forEach((x) => fl.add(x));
      (a.spf || []).forEach((x) => sp.add(x));

      min = Math.min(min, p.price);
      max = Math.max(max, p.price);
    });

    if (min === Infinity) {
      min = 0;
      max = 0;
    }

    return {
      brands: Array.from(b.entries()).sort(
        (a, b2) => b2[1] - a[1]
      ),
      skin: Array.from(sk),
      flags: Array.from(fl),
      spf: Array.from(sp).sort(),
      priceMin: Math.floor(min),
      priceMax: Math.ceil(max),
    };
  }, [scoped, attrs]);

  // =========================================================
  // RESET FILTERS WHEN CATEGORY / SEARCH CHANGES
  // =========================================================

  useEffect(() => {
    setBrands(new Set());
    setSkin(new Set());
    setFlags(new Set());
    setSpf(new Set());

    setInStock(false);
    setOnOffer(false);
    setPrice(null);
    setVisible(PAGE);

    setSort(search ? "relevance" : "discount");
  }, [category, search]);

  // =========================================================
  // PRICE
  // =========================================================

  const priceBounds = [
    facets.priceMin,
    facets.priceMax,
  ];

  const activePrice = price || priceBounds;

  // =========================================================
  // FACET HELPER
  // =========================================================

  const anyVal = (set, vals) => {
    return (
      set.size === 0 ||
      (vals || []).some((v) => set.has(v))
    );
  };

  // =========================================================
  // FILTERED PRODUCTS
  // =========================================================

  const filtered = useMemo(() => {
    let list = scoped.filter((p) => {
      const a = attrs[p.id] || {};

      if (
        brands.size &&
        !brands.has(p.brand)
      ) {
        return false;
      }

      if (inStock && p.stock <= 0) {
        return false;
      }

      if (
        onOffer &&
        !(p.compare_price && p.compare_price > p.price)
      ) {
        return false;
      }

      if (
        price &&
        (p.price < price[0] ||
          p.price > price[1])
      ) {
        return false;
      }

      if (!anyVal(skin, a.skin)) {
        return false;
      }

      if (!anyVal(flags, a.flags)) {
        return false;
      }

      if (!anyVal(spf, a.spf)) {
        return false;
      }

      return true;
    });

    if (sort === "price_asc") {
      list = [...list].sort(
        (a, b) => a.price - b.price
      );
    } else if (sort === "price_desc") {
      list = [...list].sort(
        (a, b) => b.price - a.price
      );
    } else if (sort === "name") {
      list = [...list].sort((a, b) =>
        a.name.localeCompare(b.name, "el")
      );
    } else if (sort === "discount") {
      list = [...list].sort((a, b) => {
        const da =
          a.compare_price > a.price
            ? 1 - a.price / a.compare_price
            : 0;

        const db =
          b.compare_price > b.price
            ? 1 - b.price / b.compare_price
            : 0;

        return db - da;
      });
    }

    return list;
  }, [
    scoped,
    attrs,
    brands,
    skin,
    flags,
    spf,
    inStock,
    onOffer,
    price,
    sort,
  ]);

  // =========================================================
  // INFINITE SCROLL
  // =========================================================

  useEffect(() => {
    if (!sentinel.current) {
      return;
    }

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible((v) => v + PAGE);
        }
      }
    );

    obs.observe(sentinel.current);

    return () => obs.disconnect();
  }, [filtered]);

  // =========================================================
  // SET CATEGORY
  // =========================================================

  const setCategory = (id) => {
    const np = new URLSearchParams(params);

    if (id) {
      const selectedCategory = categoryMap[id];

      if (selectedCategory?.slug) {
        window.location.href =
          "/category/" + selectedCategory.slug;

        return;
      }

      np.set("category", id);
    } else {
      np.delete("category");
    }

    np.delete("search");

    setParams(np);
  };

  // =========================================================
  // TOGGLE FILTER
  // =========================================================

  const toggle = (set, setter, val) => {
    const n = new Set(set);

    if (n.has(val)) {
      n.delete(val);
    } else {
      n.add(val);
    }

    setter(n);
    setVisible(PAGE);
  };

  // =========================================================
  // ACTIVE FILTER CHIPS
  // =========================================================

  const activeChips = [
    ...Array.from(brands).map((b) => ({
      label: b,
      clear: () =>
        toggle(brands, setBrands, b),
    })),

    ...Array.from(skin).map((s) => ({
      label: SKIN_LABELS[s] || s,
      clear: () =>
        toggle(skin, setSkin, s),
    })),

    ...Array.from(flags).map((f) => ({
      label: FLAG_LABELS[f] || f,
      clear: () =>
        toggle(flags, setFlags, f),
    })),

    ...Array.from(spf).map((s) => ({
      label: s,
      clear: () =>
        toggle(spf, setSpf, s),
    })),

    ...(inStock
      ? [
          {
            label: "Διαθέσιμα",
            clear: () => setInStock(false),
          },
        ]
      : []),

    ...(onOffer
      ? [
          {
            label: "Σε προσφορά",
            clear: () => setOnOffer(false),
          },
        ]
      : []),

    ...(price
      ? [
          {
            label: `${eur(price[0])} - ${eur(price[1])}`,
            clear: () => setPrice(null),
          },
        ]
      : []),
  ];

  // =========================================================
  // RESET ALL
  // =========================================================

  const resetAll = () => {
    setBrands(new Set());
    setSkin(new Set());
    setFlags(new Set());
    setSpf(new Set());

    setInStock(false);
    setOnOffer(false);
    setPrice(null);
    setVisible(PAGE);
  };

  // =========================================================
  // FILTER CHIP
  // =========================================================

  const FilterChip = ({
    active,
    onClick,
    children,
    count,
    icon,
    level = 0,
    hasChildren = false,
    expanded = false,
  }) => (
    <button
      onClick={onClick}
      className={`flex items-center justify-between w-full text-left rounded-lg text-sm transition-all ${
        level === 0
          ? "px-3 py-2.5"
          : "px-3 py-2"
      } ${
        active
          ? "bg-[#1E3A2F] text-white"
          : "hover:bg-[#E8F0E1] text-[#2a3a33]"
      }`}
    >
      <span className="flex items-center gap-2 min-w-0">
        {icon}

        <span
          className={
            level === 0
              ? "font-semibold"
              : "text-sm"
          }
        >
          {children}
        </span>
      </span>

      <span className="flex items-center gap-2 ml-2 shrink-0">
        {count != null && (
          <span
            className={`text-xs ${
              active
                ? "text-white/70"
                : "text-[#536D61]"
            }`}
          >
            {count}
          </span>
        )}

        {hasChildren &&
          (expanded ? (
            <ChevronDown
              className={`h-4 w-4 ${
                active
                  ? "text-white"
                  : "text-[#536D61]"
              }`}
            />
          ) : (
            <ChevronRight
              className={`h-4 w-4 ${
                active
                  ? "text-white"
                  : "text-[#536D61]"
              }`}
            />
          ))}
      </span>
    </button>
  );

  // =========================================================
  // CATEGORY ROW
  // =========================================================

  const CategoryRow = ({ c }) => {
    const hasChildren =
      (childrenMap[c.id] || []).length > 0;

    const expanded =
      expandedCategories.has(c.id);

    if (!isCategoryVisible(c)) {
      return null;
    }

    if (c.slug) {
      return (
        <div className="relative">
          <Link
            to={`/category/${c.slug}`}
            onClick={() => {
              if (hasChildren) {
                toggleCategoryOpen(c.id);
              }
            }}
            className={`flex items-center justify-between w-full text-left rounded-lg text-sm transition-all ${
              c.level === 0
                ? "px-3 py-2.5"
                : "px-3 py-2"
            } ${
              category === c.id
                ? "bg-[#1E3A2F] text-white"
                : "hover:bg-[#E8F0E1] text-[#2a3a33]"
            }`}
          >
            <span className="flex items-center gap-2 min-w-0">
              <span
                className={
                  c.level === 0
                    ? "font-semibold"
                    : "text-sm"
                }
              >
                {c.name}
              </span>
            </span>

            <span className="flex items-center gap-2 ml-2 shrink-0">
              <span
                className={`text-xs ${
                  category === c.id
                    ? "text-white/70"
                    : "text-[#536D61]"
                }`}
              >
                {categoryProductCount(c.id)}
              </span>

              {hasChildren &&
                (expanded ? (
                  <ChevronDown
                    className={`h-4 w-4 ${
                      category === c.id
                        ? "text-white"
                        : "text-[#536D61]"
                    }`}
                  />
                ) : (
                  <ChevronRight
                    className={`h-4 w-4 ${
                      category === c.id
                        ? "text-white"
                        : "text-[#536D61]"
                    }`}
                  />
                ))}
            </span>
          </Link>
        </div>
      );
    }

    return (
      <FilterChip
        active={category === c.id}
        onClick={() => {
          setCategory(c.id);

          if (hasChildren) {
            toggleCategoryOpen(c.id);
          }
        }}
        count={categoryProductCount(c.id)}
        level={c.level}
        hasChildren={hasChildren}
        expanded={expanded}
      >
        {c.name}
      </FilterChip>
    );
  };

  // =========================================================
  // FILTERS PANEL
  // =========================================================

  const FiltersPanel = () => (
    <div className="space-y-6">

      {/* CATEGORIES */}

      <div>
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#536D61] mb-2">
          Κατηγορίες
        </h3>

        <div className="space-y-0.5">

          {/* ALL PRODUCTS */}

          <Link
            to="/shop"
            onClick={() => {
              setParams({});
            }}
            className={`flex items-center justify-between w-full text-left rounded-lg text-sm transition-all px-3 py-2.5 ${
              !category
                ? "bg-[#1E3A2F] text-white"
                : "hover:bg-[#E8F0E1] text-[#2a3a33]"
            }`}
          >
            <span className="font-semibold">
              Όλα τα προϊόντα
            </span>
          </Link>

          {/* CATEGORY TREE */}

          {orderedCategories.map((c) => (
            <CategoryRow
              key={c.id}
              c={c}
            />
          ))}

        </div>
      </div>

      {/* PRICE */}

      {facets.priceMax > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#536D61] mb-3">
            Τιμή
          </h3>

          <Slider
            min={priceBounds[0]}
            max={priceBounds[1]}
            step={1}
            value={activePrice}
            onValueChange={(v) => {
              setPrice(v);
              setVisible(PAGE);
            }}
            data-testid="price-slider"
          />

          <div className="flex justify-between text-sm text-[#2a3a33] mt-2">
            <span>
              {eur(activePrice[0])}
            </span>

            <span>
              {eur(activePrice[1])}
            </span>
          </div>
        </div>
      )}

      {/* AVAILABILITY / OFFERS */}

      <div className="space-y-0.5">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#536D61] mb-2">
          Διαθεσιμότητα
        </h3>

        <FilterChip
          active={inStock}
          onClick={() => {
            setInStock((v) => !v);
            setVisible(PAGE);
          }}
        >
          Άμεσα διαθέσιμα
        </FilterChip>

        <FilterChip
          active={onOffer}
          onClick={() => {
            setOnOffer((v) => !v);
            setVisible(PAGE);
          }}
        >
          Σε προσφορά
        </FilterChip>
      </div>

      {/* BRANDS */}

      {facets.brands.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#536D61] mb-2">
            Μάρκα
          </h3>

          <div className="space-y-0.5 max-h-56 overflow-auto pr-1">
            {facets.brands.map(([b, n]) => (
              <FilterChip
                key={b}
                active={brands.has(b)}
                onClick={() =>
                  toggle(
                    brands,
                    setBrands,
                    b
                  )
                }
                count={n}
              >
                {b}
              </FilterChip>
            ))}
          </div>
        </div>
      )}

      {/* SPF */}

      {facets.spf.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#536D61] mb-2">
            Δείκτης προστασίας (SPF)
          </h3>

          <div className="space-y-0.5">
            {facets.spf.map((s) => (
              <FilterChip
                key={s}
                active={spf.has(s)}
                onClick={() =>
                  toggle(
                    spf,
                    setSpf,
                    s
                  )
                }
              >
                {s}
              </FilterChip>
            ))}
          </div>
        </div>
      )}

      {/* SKIN TYPE */}

      {facets.skin.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#536D61] mb-2">
            Τύπος δέρματος
          </h3>

          <div className="space-y-0.5">
            {facets.skin.map((s) => (
              <FilterChip
                key={s}
                active={skin.has(s)}
                onClick={() =>
                  toggle(
                    skin,
                    setSkin,
                    s
                  )
                }
              >
                {SKIN_LABELS[s] || s}
              </FilterChip>
            ))}
          </div>
        </div>
      )}

      {/* FLAGS */}

      {facets.flags.length > 0 && (
        <div>
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#536D61] mb-2">
            Χαρακτηριστικά
          </h3>

          <div className="space-y-0.5">
            {facets.flags.map((f) => (
              <FilterChip
                key={f}
                active={flags.has(f)}
                onClick={() =>
                  toggle(
                    flags,
                    setFlags,
                    f
                  )
                }
              >
                {FLAG_LABELS[f] || f}
              </FilterChip>
            ))}
          </div>
        </div>
      )}

    </div>
  );

  // =========================================================
  // PAGE
  // =========================================================

  const selectedCategory = categoryMap[category];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10">

      {/* TITLE */}

      <div className="mb-6">
        <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1E3A2F]">
          {search
            ? "Αναζήτηση"
            : selectedCategory?.name ||
              "Όλα τα προϊόντα"}
        </h1>

        {search && (
          <p className="mt-2 text-[#536D61]">
            Αποτελέσματα για: «{search}»
          </p>
        )}

        {selectedCategory?.description && !search && (
          <p className="mt-3 max-w-3xl text-[#536D61] leading-relaxed">
            {selectedCategory.description}
          </p>
        )}
      </div>

      {/* CONTENT */}

      <div className="flex flex-col lg:flex-row gap-8">

        {/* DESKTOP SIDEBAR */}

        <aside className="hidden lg:block lg:w-64 shrink-0">

          <div className="sticky top-28 max-h-[calc(100vh-8rem)] overflow-auto pr-1">

            <div className="flex items-center justify-between mb-4">

              <span className="flex items-center gap-2 text-[#1E3A2F] font-semibold">
                <SlidersHorizontal className="h-4 w-4" />
                Φίλτρα
              </span>

              {activeChips.length > 0 && (
                <button
                  onClick={resetAll}
                  data-testid="reset-filters"
                  className="text-xs text-[#536D61] hover:text-[#C25953] flex items-center gap-1"
                >
                  Καθαρισμός
                </button>
              )}

            </div>

            <FiltersPanel />

          </div>

        </aside>

        {/* MAIN */}

        <div className="flex-1 min-w-0">

          {/* TOOLBAR */}

          <div className="flex items-center justify-between gap-3 mb-4">

            <div className="flex items-center gap-2">

              {/* MOBILE FILTER */}

              <Sheet>

                <SheetTrigger asChild>

                  <button
                    data-testid="mobile-filter-trigger"
                    className="lg:hidden inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-[#E5E9E4] text-sm text-[#1E3A2F] bg-white"
                  >
                    <SlidersHorizontal className="h-4 w-4" />

                    Φίλτρα

                    {activeChips.length > 0 && (
                      <span className="bg-[#1E3A2F] text-white text-xs rounded-full px-1.5">
                        {activeChips.length}
                      </span>
                    )}
                  </button>

                </SheetTrigger>

                <SheetContent
                  side="left"
                  className="w-[88vw] max-w-sm overflow-auto bg-white"
                >

                  <SheetHeader>
                    <SheetTitle className="font-serif-display text-2xl text-[#1E3A2F]">
                      Φίλτρα
                    </SheetTitle>
                  </SheetHeader>

                  <div className="mt-4">

                    {activeChips.length > 0 && (
                      <button
                        onClick={resetAll}
                        className="text-sm text-[#C25953] mb-3 flex items-center gap-1"
                      >
                        Καθαρισμός φίλτρων
                      </button>
                    )}

                    <FiltersPanel />

                  </div>

                </SheetContent>

              </Sheet>

              <span className="text-sm text-[#536D61]">
                {filtered.length} προϊόντα
              </span>

            </div>

            {/* SORT */}

            <div className="relative">

              <select
                value={sort}
                onChange={(e) =>
                  setSort(e.target.value)
                }
                data-testid="sort-select"
                className="appearance-none bg-white border border-[#E5E9E4] rounded-full pl-4 pr-9 py-2.5 text-sm text-[#1E3A2F] focus:border-[#1E3A2F] outline-none cursor-pointer"
              >

                {SORTS.map((s) => (
                  <option
                    key={s.key}
                    value={s.key}
                  >
                    {s.label}
                  </option>
                ))}

              </select>

              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#536D61] pointer-events-none" />

            </div>

          </div>

          {/* ACTIVE FILTER CHIPS */}

          {activeChips.length > 0 && (
            <div
              className="flex flex-wrap gap-2 mb-5"
              data-testid="active-filters"
            >

              {activeChips.map((c, i) => (
                <button
                  key={i}
                  onClick={c.clear}
                  className="inline-flex items-center gap-1.5 bg-[#E8F0E1] text-[#1E3A2F] text-sm px-3 py-1.5 rounded-full hover:bg-[#d8e6c9]"
                >
                  {c.label}

                  <X className="h-3.5 w-3.5" />
                </button>
              ))}

            </div>
          )}

          {/* PRODUCTS */}

          {loading ? (

            <p className="text-[#536D61]">
              Φόρτωση...
            </p>

          ) : filtered.length > 0 ? (

            <>

              <div
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8"
                data-testid="products-grid"
              >

                {filtered
                  .slice(0, visible)
                  .map((p) => (
                    <ProductCard
                      key={p.id}
                      product={p}
                    />
                  ))}

              </div>

              {visible < filtered.length && (
                <div
                  ref={sentinel}
                  className="py-10 text-center"
                >

                  <button
                    onClick={() =>
                      setVisible(
                        (v) => v + PAGE
                      )
                    }
                    className="text-sm text-[#1E3A2F] border border-[#E5E9E4] rounded-full px-6 py-2.5 hover:bg-[#E8F0E1]"
                  >
                    Φόρτωση περισσότερων
                  </button>

                </div>
              )}

            </>

          ) : (

            <div className="text-center py-16 border border-dashed border-[#E5E9E4] rounded-2xl">

              <Pill className="h-12 w-12 text-[#1E3A2F]/30 mx-auto mb-4" />

              <p className="text-[#1E3A2F] font-medium">
                Δεν βρέθηκαν προϊόντα με αυτά τα κριτήρια.
              </p>

              <p className="text-[#536D61] text-sm mt-1">
                Δοκιμάστε να αφαιρέσετε κάποια φίλτρα.
              </p>

              {activeChips.length > 0 && (
                <button
                  onClick={resetAll}
                  className="mt-4 inline-flex items-center gap-2 bg-[#1E3A2F] text-white px-5 py-2.5 rounded-full text-sm hover:bg-[#111C18]"
                >
                  Καθαρισμός φίλτρων
                </button>
              )}

              {scoped.length === 0 &&
                all.length > 0 && (

                  <div className="mt-10 text-left">

                    <h3 className="font-serif-display text-xl text-[#1E3A2F] mb-4 text-center">
                      Προτεινόμενα προϊόντα
                    </h3>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8">

                      {all
                        .slice(0, 4)
                        .map((p) => (
                          <ProductCard
                            key={p.id}
                            product={p}
                          />
                        ))}

                    </div>

                  </div>

                )}

            </div>

          )}

        </div>

      </div>

    </div>
  );
}